<script setup lang="ts">
import { computed } from 'vue'
import { LineChart } from '@hantera/design-system'
import type { ChartSeries } from '@hantera/design-system'
import { formatting } from '@hantera/portal-app'
import type { AvailableStockResult, InventoryStockHistory, SkuBackOrder } from '@/backend/skus'

import { toNumber, toTime, INVENTORY_SERIES } from './sku-format'

interface OrderBoundEvent {
  timestamp: unknown
  quantity?: unknown
  deleted: boolean
}

const props = defineProps<{
  title?: string
  history: InventoryStockHistory
  availability?: AvailableStockResult
  backOrders?: SkuBackOrder[]
  hidden?: string[]
}>()

const DAY_MS = 24 * 60 * 60 * 1000

interface StockPoint {
  timestamp: number
  quantity: number
}

// A stepped running total of an event log: each point is the active total as of
// that timestamp.
interface TotalPoint {
  timestamp: number
  total: number
}

const now = Date.now()

// Absolute physical-stock snapshots, sorted chronologically.
const physicalSnapshots = computed<StockPoint[]>(() =>
  props.history.physicalStock
    .map((p) => ({ timestamp: toTime(p.timestamp), quantity: toNumber(p.quantity) }))
    .filter((p): p is StockPoint => p.timestamp !== null)
    .sort((a, b) => a.timestamp - b.timestamp)
)

// Folds an order-bound event log (reservations / back orders) into a stepped
// running total: each emitted point is the active total as of an event time.
// Events are partial diffs keyed by id, so quantity carries forward and the
// terminal deleted flag removes the entry's contribution.
function runningTotals(events: OrderBoundEvent[], idOf: (e: any) => string): TotalPoint[] {
  const ordered = events
    .map((e) => ({ time: toTime(e.timestamp), event: e }))
    .filter((e): e is { time: number; event: OrderBoundEvent } => e.time !== null)
    .sort((a, b) => a.time - b.time)

  const quantityById = new Map<string, number>()
  const points: TotalPoint[] = []

  for (const { time, event } of ordered) {
    const id = idOf(event)
    if (event.deleted) {
      quantityById.delete(id)
    } else if (event.quantity != null) {
      quantityById.set(id, toNumber(event.quantity))
    } else if (!quantityById.has(id)) {
      quantityById.set(id, 0)
    }

    let total = 0
    for (const q of quantityById.values()) {
      total += q
    }

    const last = points[points.length - 1]
    if (last && last.timestamp === time) {
      last.total = total
    } else {
      points.push({ timestamp: time, total })
    }
  }

  return points
}

// Stepped reservation / back-order totals over the actual past event timeline.
const reservationTotals = computed<TotalPoint[]>(() =>
  runningTotals(props.history.reservations ?? [], (e) => e.stockReservationId)
)

const backOrderTotals = computed<TotalPoint[]>(() =>
  runningTotals(props.history.backOrders ?? [], (e) => e.backOrderId)
)

const allocationTotals = computed<TotalPoint[]>(() =>
  runningTotals(props.history.allocations ?? [], (e) => e.stockAllocationId)
)

// Latest total at or before a given time, stepping through the running totals.
function totalAt(points: TotalPoint[], time: number): number {
  let value = 0
  for (const point of points) {
    if (point.timestamp <= time) {
      value = point.total
    } else {
      break
    }
  }
  return value
}

const currentReservation = computed(() => totalAt(reservationTotals.value, now))
const currentBackOrder = computed(() => totalAt(backOrderTotals.value, now))
const currentAllocation = computed(() => totalAt(allocationTotals.value, now))

// Physical level as of right now (latest snapshot at or before now).
const currentPhysical = computed(() => {
  let value = 0
  for (const point of physicalSnapshots.value) {
    if (point.timestamp <= now) {
      value = point.quantity
    }
  }
  return value
})

// Returns the physical level at a given time, stepping through the snapshots.
function physicalAt(time: number): number {
  let value = 0
  for (const point of physicalSnapshots.value) {
    if (point.timestamp <= time) {
      value = point.quantity
    } else {
      break
    }
  }
  return value
}

interface IncomingArrival {
  time: number
  quantity: number
}

// Free incoming stock arriving in the future. Incoming is not allocation-tagged,
// so the unallocated context holds the authoritative shipment list with each
// shipment's quantity already net of existing reservations.
const futureIncoming = computed<IncomingArrival[]>(() => {
  const list = props.availability?.unallocated.incomingStock ?? []
  return list
    .map((i) => ({ time: toTime(i.expectedOn), quantity: toNumber(i.availableQuantity) }))
    .filter((i): i is IncomingArrival => i.time !== null && i.quantity > 0)
    .sort((a, b) => a.time - b.time)
})

// Greedily assign incoming arrivals to outstanding back orders in the calculator's
// order (priority desc, then inventory date asc). Incoming is a shared pool, so
// each arrival can only cover one unit of demand once. Returns cumulative covered
// quantity per arrival time.
const backOrderCoverage = computed<{ time: number; covered: number }[]>(() => {
  const pool = futureIncoming.value.map((i) => ({ ...i }))
  const orders = (props.backOrders ?? [])
    .map((b) => ({
      qty: toNumber(b.quantity),
      priority: toNumber(b.priority),
      date: toTime(b.inventoryDate) ?? 0,
    }))
    .filter((b) => b.qty > 0)
    .sort((a, b) => b.priority - a.priority || a.date - b.date)

  const coveredByTime = new Map<number, number>()
  for (const order of orders) {
    let remaining = order.qty
    for (const entry of pool) {
      if (remaining <= 0) break
      if (entry.quantity <= 0) continue
      const take = Math.min(remaining, entry.quantity)
      entry.quantity -= take
      remaining -= take
      coveredByTime.set(entry.time, (coveredByTime.get(entry.time) ?? 0) + take)
    }
  }

  let cumulative = 0
  return Array.from(coveredByTime.keys())
    .sort((a, b) => a - b)
    .map((time) => {
      cumulative += coveredByTime.get(time)!
      return { time, covered: cumulative }
    })
})

// Cumulative back-order quantity covered by incoming arriving at or before a time.
function coveredAt(time: number): number {
  let value = 0
  for (const point of backOrderCoverage.value) {
    if (point.time <= time) {
      value = point.covered
    } else {
      break
    }
  }
  return value
}

// Cumulative free incoming arriving at or before a given time (future only).
function cumulativeIncomingAt(time: number): number {
  let total = 0
  for (const arrival of futureIncoming.value) {
    if (arrival.time <= time) {
      total += arrival.quantity
    } else {
      break
    }
  }
  return total
}

// Unified, sorted breakpoints so all series step on identical x positions.
const breakpoints = computed<number[]>(() => {
  const set = new Set<number>()
  for (const p of physicalSnapshots.value) {
    set.add(p.timestamp)
  }
  set.add(now)
  for (const i of futureIncoming.value) {
    if (i.time >= now) {
      set.add(i.time)
    }
  }
  for (const c of backOrderCoverage.value) {
    set.add(c.time)
  }
  // Reservations and back orders step on their own historical event times.
  for (const r of reservationTotals.value) {
    set.add(r.timestamp)
  }
  for (const b of backOrderTotals.value) {
    set.add(b.timestamp)
  }
  for (const a of allocationTotals.value) {
    set.add(a.timestamp)
  }

  // Ensure a visible future horizon even when there are no incoming shipments.
  const lastFuture = futureIncoming.value.length > 0
    ? futureIncoming.value[futureIncoming.value.length - 1].time
    : now
  set.add(Math.max(lastFuture, now + 30 * DAY_MS))

  return Array.from(set).sort((a, b) => a - b)
})

const physicalSeries = computed(() =>
  breakpoints.value.map((t) => ({
    x: t,
    // Past/present: actual stepped level. Future: held at the current physical level.
    y: t <= now ? physicalAt(t) : currentPhysical.value,
  }))
)

const incomingSeries = computed(() =>
  breakpoints.value.map((t) => ({
    x: t,
    // Only project incoming into the future, cumulative as shipments arrive.
    y: t < now ? 0 : cumulativeIncomingAt(t),
  }))
)

// Reservation overlay: stepped historical total. Into the future the reserved
// amount grows as incoming arrivals cover outstanding back orders.
const reservationSeries = computed(() =>
  breakpoints.value.map((t) => ({
    x: t,
    y: t <= now ? totalAt(reservationTotals.value, t) : currentReservation.value + coveredAt(t),
  }))
)

const allocationSeries = computed(() =>
  breakpoints.value.map((t) => ({
    x: t,
    y: t <= now ? totalAt(allocationTotals.value, t) : currentAllocation.value,
  }))
)

// Back orders mirror reservations but render below zero as a negative area. Into
// the future the back order shrinks toward zero as incoming covers it.
const backOrderSeries = computed(() =>
  breakpoints.value.map((t) => ({
    x: t,
    y: -(t <= now ? totalAt(backOrderTotals.value, t) : Math.max(0, currentBackOrder.value - coveredAt(t))),
  }))
)

const hasData = computed(() =>
  physicalSnapshots.value.length > 0 ||
  futureIncoming.value.length > 0 ||
  reservationTotals.value.length > 0 ||
  allocationTotals.value.length > 0 ||
  backOrderTotals.value.length > 0
)

const series = computed<ChartSeries[]>(() => [
  {
    label: INVENTORY_SERIES.physical.label,
    stackGroup: 'stock',
    area: true,
    color: INVENTORY_SERIES.physical.color,
    points: physicalSeries.value.map((p) => ({ label: String(p.x), x: p.x, value: p.y })),
  },
  {
    label: INVENTORY_SERIES.incoming.label,
    stackGroup: 'stock',
    area: true,
    color: INVENTORY_SERIES.incoming.color,
    points: incomingSeries.value.map((p) => ({ label: String(p.x), x: p.x, value: p.y })),
  },
  {
    label: INVENTORY_SERIES.reservations.label,
    stackGroup: 'reserved',
    area: true,
    color: INVENTORY_SERIES.reservations.color,
    points: reservationSeries.value.map((p) => ({ label: String(p.x), x: p.x, value: p.y })),
  },
  {
    label: INVENTORY_SERIES.allocated.label,
    stackGroup: 'reserved',
    area: true,
    color: INVENTORY_SERIES.allocated.color,
    points: allocationSeries.value.map((p) => ({ label: String(p.x), x: p.x, value: p.y })),
  },
  {
    label: INVENTORY_SERIES.backOrders.label,
    area: true,
    color: INVENTORY_SERIES.backOrders.color,
    points: backOrderSeries.value.map((p) => ({ label: String(p.x), x: p.x, value: p.y })),
  },
])

const visibleSeries = computed(() =>
  series.value.filter((s) => !(props.hidden ?? []).includes(s.label))
)

const referenceLines = [{ x: now, label: 'Today' }]

const formatLabel = (label: string) => formatting.date(new Date(Number(label)))
</script>

<template>
  <div class="chart-container">
    <LineChart
      v-if="hasData"
      :title="title"
      :series="visibleSeries"
      :stepped="true"
      :reference-lines="referenceLines"
      :format-label="formatLabel"
      :height="240"
      :legend="false"
    />
    <div v-else class="no-data">No data</div>
  </div>
</template>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-data {
  color: hsl(var(--color-40));
  font-size: 14px;
}
</style>
